import { styled, Switch, SwitchProps } from '@mui/material'
import { forwardRef } from 'react'

type CustomSize = 'small' | 'medium' | 'large'

interface ToggleSwitchProps extends Omit<SwitchProps, 'size'> {
    size?: CustomSize
}

const getSizeConfig = (size: CustomSize = 'medium') => {
    const configs = {
        small: {
            width: 20,
            height: 12,
            thumbSize: 8,
            thumbRadius: 4,
            checkedTransform: 8,
            activeTransform: 6,
            activeThumbWidth: 10
        },
        medium: {
            width: 28,
            height: 16,
            thumbSize: 12,
            thumbRadius: 6,
            checkedTransform: 12,
            activeTransform: 9,
            activeThumbWidth: 15
        },
        large: {
            width: 36,
            height: 20,
            thumbSize: 16,
            thumbRadius: 8,
            checkedTransform: 16,
            activeTransform: 12,
            activeThumbWidth: 19
        }
    }
    return configs[size]
}

const StyledSwitch = styled(Switch, {
    shouldForwardProp: prop => prop !== 'customSize'
})<{ customSize: CustomSize }>(({ theme, customSize = 'medium' }) => {
    const config = getSizeConfig(customSize)

    return {
        width: config.width,
        height: config.height,
        padding: 0,
        display: 'flex',
        '&:active': {
            '& .MuiSwitch-thumb': {
                width: config.activeThumbWidth
            },
            '& .MuiSwitch-switchBase.Mui-checked': {
                transform: `translateX(${config.activeTransform}px)`
            }
        },
        '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': {
                transform: `translateX(${config.checkedTransform}px)`,
                color: '#fff',
                '& + .MuiSwitch-track': {
                    opacity: 1,
                    backgroundColor: 'primary.main',
                    ...theme.applyStyles('dark', {
                        backgroundColor: 'primary.main'
                    })
                }
            }
        },
        '& .MuiSwitch-thumb': {
            boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
            width: config.thumbSize,
            height: config.thumbSize,
            borderRadius: config.thumbRadius,
            transition: theme.transitions.create(['width'], {
                duration: 200
            })
        },
        '& .MuiSwitch-track': {
            borderRadius: config.height / 2,
            opacity: 1,
            backgroundColor: 'rgba(0,0,0,.25)',
            boxSizing: 'border-box',
            ...theme.applyStyles('dark', {
                backgroundColor: 'rgba(255,255,255,.35)'
            })
        }
    }
})

export const ToggleSwitch = forwardRef<HTMLButtonElement, ToggleSwitchProps>(({ size = 'medium', ...props }, ref) => {
    return <StyledSwitch {...props} customSize={size} ref={ref} />
})

ToggleSwitch.displayName = 'ToggleSwitch'
